import { useCallback, useEffect, useState } from "react";
import { fetchApprovalDetail } from "@/lib/api";
import type { ApprovalDetail, ApprovalStatus } from "@/types";
import { MOCK_APPROVALS, MOCK_APPROVAL_DETAILS } from "@/data/mock";

interface UseApprovalDetailResult {
  detail: ApprovalDetail | null;
  status: ApprovalStatus;
  loading: boolean;
  error: string | null;
  source: "api" | "mock";
  refresh: () => void;
}

function toApprovalStatus(raw: unknown): ApprovalStatus {
  const value = String(raw ?? "pending").toLowerCase();
  if (value.includes("approved")) return "approved";
  if (value.includes("reject")) return "rejected";
  if (value.includes("escalat")) return "escalated";
  return "pending";
}

function mockApprovalStatus(id: string): ApprovalStatus {
  return MOCK_APPROVALS.find((a) => a.id.toUpperCase() === id.toUpperCase())?.status ?? "pending";
}

function mapApiApprovalDetail(raw: Record<string, unknown>): ApprovalDetail {
  const id = String(raw.id ?? raw.approvalId ?? "");
  const approvalId = String(raw.approvalId ?? raw.id ?? "");
  const transferId = String(raw.transferId ?? raw.entityId ?? "");
  const amount = String(raw.amount ?? "");
  const source = String(raw.source ?? raw.sourceWallet ?? "");
  const destination = String(raw.destination ?? raw.destinationAddress ?? "");
  const requester = String(raw.requester ?? raw.requestedBy ?? "");
  const memo = String(raw.memo ?? raw.note ?? "");
  const risk = String(raw.risk ?? "low") as ApprovalDetail["risk"];
  const policyTitle = String(raw.policyTitle ?? raw.policy ?? "");
  const policyDesc = String(raw.policyDesc ?? raw.policyDescription ?? "");
  const screeningTitle = String(raw.screeningTitle ?? "Screening");
  const screeningDesc = String(raw.screeningDesc ?? raw.screeningDescription ?? "");
  const quorumLabel = String(raw.quorumLabel ?? "");
  const quorumPct = typeof raw.quorumPct === "number" ? raw.quorumPct : 0;

  const approvers = Array.isArray(raw.approvers)
    ? (raw.approvers as Record<string, unknown>[]).map(a => ({
        initials: String(a.initials ?? ""),
        name: String(a.name ?? ""),
        role: String(a.role ?? ""),
        status: String(a.status ?? "available") as "action" | "available",
        you: a.you === true,
      }))
    : [];

  return {
    id, approvalId, transferId, amount, source, destination,
    requester, memo, risk, policyTitle, policyDesc,
    screeningTitle, screeningDesc, quorumLabel, quorumPct, approvers,
  };
}

export function useApprovalDetail(id: string): UseApprovalDetailResult {
  const [detail, setDetail] = useState<ApprovalDetail | null>(null);
  const [status, setStatus] = useState<ApprovalStatus>("pending");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<"api" | "mock">("api");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchApprovalDetail(id);
      if (result.approval) {
        setDetail(mapApiApprovalDetail(result.approval));
        setStatus(toApprovalStatus(result.approval.status));
        setSource("api");
      } else {
        const mock = MOCK_APPROVAL_DETAILS.find(d => d.id.toUpperCase() === id.toUpperCase());
        if (mock) {
          setDetail(mock);
          setStatus(mockApprovalStatus(id));
          setSource("mock");
        } else {
          setError(result.reasonCode || "Approval not found");
          setDetail(null);
        }
      }
    } catch {
      const mock = MOCK_APPROVAL_DETAILS.find(d => d.id.toUpperCase() === id.toUpperCase());
      if (mock) {
        setDetail(mock);
        setStatus(mockApprovalStatus(id));
        setSource("mock");
      } else {
        setError("Failed to load approval");
        setDetail(null);
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  return { detail, status, loading, error, source, refresh: load };
}
